// src/context/DiscountContext.jsx - Shared discount data context

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { discountApi } from '../services/api';

const DiscountContext = createContext();

export const useDiscounts = () => {
  const context = useContext(DiscountContext);
  if (!context) {
    throw new Error('useDiscounts must be used within a DiscountProvider');
  }
  return context;
};

export const DiscountProvider = ({ children }) => {
  const [activeDiscounts, setActiveDiscounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);

  const loadActiveDiscounts = useCallback(async (forceRefresh = false) => {
    // Check if cache version has changed (backend discounts updated)
    const currentVersion = localStorage.getItem('discount-cache-version');
    const lastVersion = localStorage.getItem('last-known-discount-version');
    
    // Force refresh if version changed or if recently fetched is over 1 minute old
    const shouldForceRefresh = forceRefresh || 
                               (currentVersion && lastVersion && currentVersion !== lastVersion) ||
                               !lastFetched || 
                               Date.now() - lastFetched > 60000;
    
    // Avoid duplicate calls if recently fetched (within 1 minute) and not forcing refresh
    if (!shouldForceRefresh && lastFetched && Date.now() - lastFetched < 60000) {
      return activeDiscounts;
    }

    try {
      setLoading(true);
      setError(null);
      
      const discounts = await discountApi.getActiveDiscounts();
      setActiveDiscounts(discounts || []);
      setLastFetched(Date.now());
      
      // Update last known version
      if (currentVersion) {
        localStorage.setItem('last-known-discount-version', currentVersion);
      }

      return discounts || [];
    } catch (err) {
      setError(err);
      setActiveDiscounts([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [lastFetched, activeDiscounts]);

  // Load discounts on mount
  useEffect(() => {
    loadActiveDiscounts();
  }, []);

  // Periodic check for discount version changes (every 30 seconds)
  useEffect(() => {
    const checkVersionInterval = setInterval(() => {
      const currentVersion = localStorage.getItem('discount-cache-version');
      const lastVersion = localStorage.getItem('last-known-discount-version');
      
      if (currentVersion && lastVersion && currentVersion !== lastVersion) {
        console.log('Discount version change detected, refreshing...');
        loadActiveDiscounts(true);
      }
    }, 30000); // Check every 30 seconds
    
    return () => clearInterval(checkVersionInterval);
  }, [loadActiveDiscounts]);

  // Provide methods to refresh discounts when needed
  const refreshDiscounts = useCallback(() => {
    return loadActiveDiscounts(true);
  }, [loadActiveDiscounts]);

  // Get public-facing discounts (for banners)
  const getPublicDiscounts = useCallback(() => {
    return activeDiscounts.filter(discount => 
      discount.isActive && 
      (!discount.endDate || new Date(discount.endDate) > new Date())
    );
  }, [activeDiscounts]);

  // Get applicable discounts for cart calculation
  const getCartDiscounts = useCallback(() => {
    return activeDiscounts.filter(discount => discount.isActive);
  }, [activeDiscounts]);

  const value = {
    // State
    activeDiscounts,
    loading,
    error,
    lastFetched,
    
    // Methods
    loadActiveDiscounts,
    refreshDiscounts,
    getPublicDiscounts,
    getCartDiscounts,
    
    // Computed values
    hasDiscounts: activeDiscounts.length > 0,
    publicDiscountCount: getPublicDiscounts().length
  };

  return (
    <DiscountContext.Provider value={value}>
      {children}
    </DiscountContext.Provider>
  );
};

export default DiscountContext;